import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Select, TreeSelect } from 'antd';
import { Category } from '../../../models/Category/Category';
import { Filter } from '../../../models/Filter/Filter';
import { Feature } from '../../../models/Feature/Feature';
import { Button, Col, Row, FormGroup, FormLabel } from 'react-bootstrap';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { ErrorMessageLabel } from '../../components/ErrorMessageLabel/ErrorMessageLabel';
import { InputField } from '../../components/InputField/InputField';
import { ToastContainer, toast } from 'react-toastify';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';

// -- Types
interface FeatureValueForm {
  value: string;
  filter_values: number[];
}
interface EditFeatureValues {
  categories: number[];
  name: string;
  values: FeatureValueForm[];
}
interface FilterValue {
  id: number;
  value: string;
}
interface FilterData {
  id: number;
  name: string;
  options?: FilterValue[];
}

function EditFeature() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();

  // Data queries
  const { data: feature, isLoading } = useQuery(['feature', id], () =>
    Feature.get(id!)
  );
  const { data: filters, isLoading: isFiltersLoading } = useQuery<FilterData[]>(
    ['filters'],
    () => Filter.all('values')
  );
  const { data: categories } = useQuery(['categories'], () => Category.all());

  if (isLoading || isFiltersLoading) {
    return <h4>Loading...</h4>;
  }

  // -- Formik initial values
  const initialValues: EditFeatureValues = {
    categories: feature?.categories?.map((cat: any) => cat.id) ?? [],
    name: feature?.name ?? '',
    values:
      feature?.options?.map((fv: any) => ({
        value: fv.value,
        filter_values:
          (fv.filterValues?.map((fvOpt: any) => fvOpt.id) as number[]) ?? []
      })) ?? []
  };

  return (
    <>
      <h1 className="cs-title">Izmena Karakteristike</h1>
      <Row>
        <Col lg={12}>
          <Formik<EditFeatureValues>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object({
              name: Yup.string().max(255, 'name.max').required('name.required'),
              categories: Yup.array().of(Yup.number()).optional(),
              values: Yup.array()
                .min(1, 'Unesite bar jednu vrednost!')
                .of(
                  Yup.object().shape({
                    value: Yup.string().required('value.required'),
                    filter_values: Yup.array().of(Yup.number()).optional()
                  })
                )
            })}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);

              // Just in case, flatten any accidental objects in filter_values
              const payload = {
                ...values,
                values: values.values.map((fv) => ({
                  value: fv.value,
                  filter_values: (
                    fv.filter_values as (number | { id: number })[]
                  ).map((fvId) =>
                    typeof fvId === 'object' && fvId !== null ? fvId.id : fvId
                  )
                }))
              };

              try {
                await feature?.update(payload);
                toast.success('Karakteristika uspešno izmenjena!');
                await client.invalidateQueries({ queryKey: ['features'] });
                await client.invalidateQueries({ queryKey: ['feature', id] });
                navigate('/admin/features/list');
              } catch (error: any) {
                toast.error(
                  error?.response?.data?.message || 'Greška pri snimanju!'
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue }) => (
              <Form autoComplete="off" className="cs-form-wrapper">
                <FormGroup>
                  <FormLabel>Izaberi kategoriju</FormLabel>
                  <TreeSelect
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 500, overflow: 'auto' }}
                    treeCheckable
                    showSearch
                    treeDefaultExpandAll
                    placeholder="Izaberi kategoriju"
                    treeData={categories ?? []}
                    value={values.categories}
                    onChange={(value) =>
                      setFieldValue('categories', value, true)
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Naziv Karakteristike</FormLabel>
                  <InputField
                    placeholder="Ime Karakteristike"
                    name="name"
                    autoComplete="off"
                  />
                  <ErrorMessageLabel name="name" />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Vrednosti Karakteristike</FormLabel>
                  <FieldArray name="values">
                    {({ push, remove }) => (
                      <>
                        {values.values?.map((val, index) => (
                          <Row key={index} className="mb-2">
                            <Col className="cs-attribute-name-wrapper">
                              <InputField
                                placeholder="Feature value"
                                name={`values.${index}.value`}
                                autoComplete="off"
                              />
                              <ErrorMessageLabel
                                name={`values.${index}.value`}
                              />
                            </Col>
                            <Col className="cs-attribute-filters-wrapper">
                              <Field
                                name={`values.${index}.filter_values`}
                                value={val.filter_values}
                                component={Select}
                                mode="multiple"
                                onChange={(value: number[]) =>
                                  setFieldValue(
                                    `values.${index}.filter_values`,
                                    value,
                                    true
                                  )
                                }
                              >
                                {filters?.map(({ id, name, options }) => (
                                  <Select.OptGroup key={id} label={name}>
                                    {options?.map(({ id, value }) => (
                                      <Select.Option key={id} value={id}>
                                        {value}
                                      </Select.Option>
                                    ))}
                                  </Select.OptGroup>
                                ))}
                              </Field>
                              <Button
                                className="mx-2 JHover"
                                variant="outline-light"
                                onClick={() => remove(index)}
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <div>
                          <Button
                            variant="outline-light"
                            className="mt-2 btn-lg JHover"
                            onClick={() =>
                              push({
                                value: '',
                                filter_values: []
                              })
                            }
                          >
                            <i className="fa-solid fa-circle-plus"></i>
                          </Button>
                        </div>
                      </>
                    )}
                  </FieldArray>
                </FormGroup>

                <ButtonPrimary type="submit">Sačuvaj</ButtonPrimary>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  );
}

export default EditFeature;
